import { createSlice } from "@reduxjs/toolkit";
import { linkIdForTask } from "../functions/hubCustomField";

const initialState = {
    dashboardSettings: null,
    dashboards: [],
    adminDashboards: [],
    dashboardsLoading: false,
    loadedAllDashboards: 0,
    loadedSettings: false,
    listForNewTask: null, //parents, donors, students, etc.
    listForNewTaskLoading: false,
    reminderTypes: null,
    reminderTypesLoading: false,
    reminderNames: null,
    users: []
    ,
    dashboardFilterSetsById: {}, // { [dashboardSettingId]: DashboardFilterSet[] }
    /** True while GET /filters is in flight for that dashboard (avoids bootstrap on pre-fetch []) */
    dashboardFilterSetsLoadingById: {}, // { [dashboardSettingId]: boolean }
    /** rules: inline-only when selectedFilterSetId is set (chips); otherwise rules are the full active filter */
    dashboardActiveFilterRulesById: {}, // { [dashboardSettingId]: { rules: Rule[], logicalOperator: 'AND'|'OR', selectedFilterSetId?: number } }
    /** Search box committed term (3+ chars, or ''). Shared with task-details next/prev. */
    dashboardSearchTermById: {}, // { [dashboardSettingId]: string }
    /** Assigned-to-me toggle. Shared with task-details next/prev. */
    dashboardAssignedToMeById: {}, // { [dashboardSettingId]: boolean }
    /** Unread @mention toggle. Shared with task-details next/prev. */
    dashboardUnseenMentionsById: {}, // { [dashboardSettingId]: boolean }
}
const hubSlice = createSlice({
    name: "hub",
    initialState,
    reducers: {
        loadDashboardSettings(state, action) {
            state.dashboardSettings = action.payload
        },
        loadDashboards(state, action){
            state.dashboards = action.payload.dashboards.filter(d => !d.AdminDashboard)
            state.adminDashboards = action.payload.dashboards.filter(d => d.AdminDashboard)
            state.loadedSettings = true
            state.loadedAllDashboards += 1
        },
        replaceTask(state, action){
            //payload should be {dashboardId: 0, task: {}}
            let dashboard = state.dashboards.find(d => d.DashboardSettingID === action.payload.dashboardId)
            if(dashboard){
                let task = dashboard.Tasks.find(t => t.DonorID === action.payload.task.DonorID)
                if(task){
                    task = action.payload.task
                }
            }
        },
        removeTaskFromList(state, action){
            //payload should be {dashboardId: 0, ID: 0}
            let dashboard = state.dashboards.find(d => d.DashboardSettingID === action.payload.dashboardId)
            if(dashboard){
                dashboard.Tasks = dashboard.Tasks.filter(t => t.ID !== action.payload.ID)
            }
        },
        setDashboardsLoading(state, action){
            state.dashboardsLoading = action.payload
        },
        loadListForNewTask(state, action) {
            state.listForNewTask = action.payload.list;
        },
        setListForNewTaskLoading(state, action){
            state.listForNewTaskLoading = action.payload
        },
        loadReminderTypes(state, action) {
            state.reminderTypes = action.payload.types.map(t => {
                return {name: t, value: t}
            });
        },
        loadReminderNames(state, action){
            state.reminderNames = action.payload.names.map(t => {
                return {name: t, value: t}
            });
        },
        setReminderTypesLoading(state, action){
            state.reminderTypesLoading = action.payload
        },
        loadUsers(state, action){
            state.users = action.payload
        },
        setDashboardSort(state, action) {
            const { dashboardId, fieldName, direction } = action.payload;
            // Look in both regular dashboards and admin dashboards
            const dashboard = state.dashboards.find(d => d.DashboardSettingID === dashboardId) ||
                            state.adminDashboards.find(d => d.DashboardSettingID === dashboardId);
            if (!dashboard) return;

            const current = dashboard.sortConfig || { field: null, direction: 'asc' };
            
            // If direction is explicitly provided, use it
            // Otherwise, toggle if same field, or default to 'asc' for new field
            let newDir;
            if (direction !== undefined) {
                newDir = direction;
            } else {
                newDir = current.field === fieldName && current.direction === 'asc' ? 'desc' : 'asc';
            }

            dashboard.sortConfig = {
                field: fieldName,
                direction: newDir
            };
        },
        setDashboardFilterSetsLoading(state, action) {
            const { dashboardId, loading } = action.payload;
            state.dashboardFilterSetsLoadingById[dashboardId] = !!loading;
        },
        setDashboardFilterSets(state, action) {
            const { dashboardId, filterSets } = action.payload;
            state.dashboardFilterSetsById[dashboardId] = filterSets || [];
            state.dashboardFilterSetsLoadingById[dashboardId] = false;
        },
        setDashboardActiveFilters(state, action) {
            const { dashboardId, rules, logicalOperator, selectedFilterSetId } = action.payload;
            state.dashboardActiveFilterRulesById[dashboardId] = {
                rules: rules || [],
                logicalOperator: (logicalOperator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND',
                selectedFilterSetId: selectedFilterSetId ?? undefined
            };
        },
        clearDashboardActiveFilters(state, action) {
            const { dashboardId } = action.payload;
            state.dashboardActiveFilterRulesById[dashboardId] = {
                rules: [],
                logicalOperator: 'AND',
                selectedFilterSetId: undefined
            };
        },
        setDashboardSearchTerm(state, action) {
            const { dashboardId, searchTerm } = action.payload;
            if (!state.dashboardSearchTermById) {
                state.dashboardSearchTermById = {};
            }
            state.dashboardSearchTermById[dashboardId] = searchTerm || '';
        },
        setDashboardAssignedToMe(state, action) {
            const { dashboardId, assignedToMe } = action.payload;
            if (!state.dashboardAssignedToMeById) {
                state.dashboardAssignedToMeById = {};
            }
            state.dashboardAssignedToMeById[dashboardId] = !!assignedToMe;
        },
        setDashboardUnseenMentions(state, action) {
            const { dashboardId, unseenMentions } = action.payload;
            if (!state.dashboardUnseenMentionsById) {
                state.dashboardUnseenMentionsById = {};
            }
            state.dashboardUnseenMentionsById[dashboardId] = !!unseenMentions;
        },
        clearUnseenMention(state, action) {
            const reminderId = action.payload && action.payload.reminderId;
            if (!reminderId) return;
            const clear = (list) => {
                (list || []).forEach((dash) => {
                    (dash.Tasks || []).forEach((task) => {
                        if (task.ReminderID === reminderId) task.HasUnseenMention = false;
                    });
                });
            };
            clear(state.dashboards);
            clear(state.adminDashboards);
        },
        updateDashboardFields(state, action) {
            const { dashboardId, fields } = action.payload;
            const dash =
                state.dashboards.find((d) => d.DashboardSettingID === dashboardId) ||
                state.adminDashboards.find((d) => d.DashboardSettingID === dashboardId);
            if (dash) {
                dash.Fields = fields || [];
            }
        },
        patchHubCustomField(state, action) {
            const { dashboardId, linkId, fieldName, value } = action.payload;
            const dash =
                state.dashboards.find((d) => d.DashboardSettingID === dashboardId) ||
                state.adminDashboards.find((d) => d.DashboardSettingID === dashboardId);
            if (!dash || !fieldName) return;

            const wanted = fieldName.toLowerCase();
            const layout = (dash.Fields || []).find(
                (f) => (f.FieldName || '').toLowerCase() === wanted
            );
            const layoutId = layout && layout.FieldId ? layout.FieldId : 0;
            const workWith = dash.Settings && dash.Settings.WorkWith;

            (dash.Tasks || []).forEach((task) => {
                if (linkIdForTask(workWith, task) !== linkId) return;
                if (!task.FieldsToDisplay) task.FieldsToDisplay = [];
                const shown = task.FieldsToDisplay.find(
                    (f) => (f.FieldName || '').toLowerCase() === wanted
                );
                if (shown) shown.Value = value;
                else task.FieldsToDisplay.push({ FieldName: fieldName, Value: value });

                if (layoutId) {
                    if (!task.FilterFieldValuesById) task.FilterFieldValuesById = {};
                    task.FilterFieldValuesById[layoutId] = value;
                }
            });
        },
        resetState:() => initialState,
    },
});

export const hubActions = hubSlice.actions;

export default hubSlice;
